import { useIntersectionObserver, useThrottledCallback } from "@react-hookz/web";
import { useCallback, useEffect, useState } from "react";

import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import type { Event } from "~/components/tracking/google-analytics/types";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import type { Event as GA4Event } from "~/components/tracking/google-analytics-4/types";

interface TrackImpressionEventByIntersectionOptions {
  intersectionThreshold?: number[];
  isSendGA4Tracking?: boolean;
  isSendGATracking?: boolean;
  shouldSendOnce?: boolean;
  shouldThrottle?: boolean;
}

interface TrackImpressionEventByIntersectionResult {
  captureTrackImpressionEventTargetElement: (element: HTMLElement | null) => void;
}

export interface UseTrackImpressionEventByIntersectionParameters {
  getEvent: (() => Event) | null;
  getGa4Event?: (() => GA4Event) | null;
  options?: TrackImpressionEventByIntersectionOptions;
}

export const useTrackImpressionEventByIntersection = (
  parameters: UseTrackImpressionEventByIntersectionParameters,
): TrackImpressionEventByIntersectionResult => {
  const { getEvent, getGa4Event, options } = parameters;
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [hasSent, setHasSent] = useState(false);

  const shouldSend = options?.shouldSendOnce ? !hasSent : true;
  const intersection = useIntersectionObserver(
    shouldSend && (getEvent || getGa4Event) ? targetElement : null,
    {
      threshold: options?.intersectionThreshold,
    },
  );

  const handleSend = useThrottledCallback(
    (event: Event | null, ga4Event: GA4Event | null) => {
      if (options?.isSendGATracking && event) {
        sendGATracking(event);
      }
      if (options?.isSendGA4Tracking && ga4Event) {
        sendGA4Tracking(ga4Event);
      }
      setHasSent(true);
    },
    [sendGATracking, sendGA4Tracking, setHasSent],
    options?.shouldThrottle ? 300 : 0,
    true,
  );

  useEffect(() => {
    if (!shouldSend || !intersection?.isIntersecting || (!getEvent && !getGa4Event)) return;
    handleSend(getEvent ? getEvent() : null, getGa4Event ? getGa4Event() : null);
  }, [getEvent, getGa4Event, shouldSend, intersection?.isIntersecting, handleSend]);

  useEffect(() => {
    setHasSent(false);
  }, [targetElement]);

  const captureTrackImpressionEventTargetElement = useCallback(
    (element: HTMLElement | null) => {
      setTargetElement(element);
    },
    [setTargetElement],
  );

  return { captureTrackImpressionEventTargetElement };
};
